import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  buildNotificationsFromPropostas,
  dismissAll,
  markAllAsRead,
  markAsRead,
  type NotifType,
  type Notification,
} from "../lib/notifications";
import { STORAGE_KEY_ID, STORAGE_KEY_META, readActiveMatricula } from "../lib/matricula-data";
import { atlas } from "../lib/sdk";
import type { EstadoProposta, Proposta } from "../lib/propostas-data";

/** Mesmo mapeamento de /servidor/contratos — situacao backend -> estado UI.
 *  ORDEM IMPORTA: cancel/recus/reprov/rejeit/negad devem casar antes de qualquer
 *  outra checagem. E "aprov" deve casar ANTES de "aguard" pois "Aguardando ADF"
 *  ja e um estado pos-aprovacao. */
function mapSituacao(situacao: string): EstadoProposta {
  const t = situacao.toLowerCase();
  if (t.includes("cancel") || t.includes("estorn")) return "cancelada";
  if (t.includes("recus") || t.includes("reprov") || t.includes("rejeit") || t.includes("negad")) return "recusada";
  if (t.includes("suspens")) return "cancelada";
  if (t.includes("expir")) return "expirada";
  // Fins de um legado (portado / a folha parou de descontar): nunca sao etapa
  // de proposta. Sem o ramo caiam no default "em_analise".
  if (t.includes("substitu") || t.includes("encerrado por aus")) return "encerrada";
  if (t.includes("quitad")) return "liberada";
  if (t.includes("ativo") || t.includes("averb")) return "liberada";
  if (t.includes("formaliz")) return "aguardando_formalizacao";
  if (t.includes("aprov")) return "aprovada"; // banco aprovou, aguarda ADF
  if (t.includes("aguard")) return "em_analise";
  return "em_analise";
}

const ICONS: Record<NotifType, string> = {
  proposta_enviada: "📤",
  proposta_em_analise: "📋",
  proposta_aprovada: "✅",
  proposta_aguardando_adf: "⏳",
  proposta_averbada: "🎉",
  proposta_aguardando_formalizacao: "📝",
  proposta_recusada: "❌",
  proposta_cancelada: "🚫",
  proposta_expirando: "⏰",
  proposta_editada_pelo_banco: "✏️",
  folha_processada: "💼",
  oferta_banco: "🎁",
  portabilidade_a_confirmar: "🔁",
  portabilidade_concluida: "✅",
};

export function NotificationBell() {
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Matricula ativa entra no queryKey → trocar matricula invalida o cache
  // automaticamente e o backend filtra por essa matricula (evita vazamento
  // de proposta entre matriculas do mesmo CPF).
  const [matAtiva, setMatAtiva] = useState<string | null>(() => readActiveMatricula()?.matricula ?? null);
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY_META || e.key === STORAGE_KEY_ID) {
        setMatAtiva(readActiveMatricula()?.matricula ?? null);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Fonte unica com /servidor/contratos: mesma query, mesmo cache.
  // Poll rapido (5s) pra sensacao de tempo real — qualquer transicao de
  // estado (banco aceita, altera, recusa) aparece no sino em ate 5s.
  const q = useQuery({
    queryKey: ["servidor", "propostas", matAtiva],
    queryFn: () => atlas.servidor.propostas(matAtiva ?? undefined),
    refetchInterval: 5_000,
    refetchOnWindowFocus: true,
    placeholderData: (prev) => prev,
  });

  // Ofertas de credito criadas pelos bancos que casam com o perfil da matricula.
  // Cliente 06/08/2026: o corte de backlog vale SO pra quem ativou a conta
  // pelo fluxo de primeiro-acesso (contaAtivadaEm) — ofertas criadas ANTES da
  // ativacao ficam escondidas. Quem ja tinha cadastro (campo null) ve todas.
  // `matAtiva` vai na CHAMADA, nao so' na queryKey. Sem isso o backend caia na
  // matricula do JWT (a do login) enquanto a chave do cache dizia outra: quem
  // acumula cargos trocava de matricula no seletor, o React Query buscava de
  // novo, e a resposta continuava sendo a da matricula errada. Oferta dirigida
  // ao convenio da OUTRA matricula nunca aparecia no sino — mas aparecia no
  // marketplace, que sempre passou o parametro (marketplace-portabilidade.tsx).
  // A query de propostas logo acima ja fazia certo; so' esta ficou de fora.
  const ofertasQ = useQuery({
    queryKey: ["servidor", "ofertas-banco", matAtiva],
    queryFn: () => atlas.servidor.getMyOfertasBanco(matAtiva ?? undefined),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    placeholderData: (prev) => prev,
  });

  // Portabilidade e' por pessoa (todas as matriculas do CPF), como na tela dela.
  const portQ = useQuery({
    queryKey: ["servidor", "portabilidade"],
    queryFn: () => atlas.portabilidade.minhas(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    placeholderData: (prev) => prev,
  });

  const propostas: Proposta[] = useMemo(
    () =>
      (q.data?.propostas ?? []).map((p) => ({
        id: p.id,
        banco: p.banco,
        estado: mapSituacao(p.situacao),
        valor: p.valor,
        parcelas: p.parcelas,
        parcela: p.parcela,
        taxaAm: p.taxaAm,
        // Prefer criado_em_iso (com hora/min/seg exatos) — sem isso, o tsDe no
        // notifications.ts caia em midnight pra TODAS as propostas do mesmo dia
        // e o sort desc virava empate; a ordem final era a de insercao, nao
        // cronologica. Cliente reportou 30/07/2026: proposta 9161730 (mais
        // recente) aparecia ABAIXO de 9020160 no sino.
        criadaEm: p.criado_em_iso ?? p.data,
        expiraEm: p.expira_em ?? undefined,
        // Datas reais de cada transicao — sem isso toda etapa herdava a data
        // de criacao ("aprovada ha 6d" numa proposta aprovada ontem).
        situacaoEm: p.situacao_em ?? null,
        // Snapshot da edicao pelo banco — dispara notif "banco atualizou os
        // valores" com antes/depois no builder.
        editadaPeloBanco: p.editadaPeloBanco ?? null,
      })),
    [q.data],
  );

  const [tickKey, setTickKey] = useState(0);
  const contaAtivadaTs = useMemo(() => {
    // Timestamp da ativacao da conta via primeiro-acesso (mesmo pra todas as
    // matriculas do mesmo CPF — persistido em jsonb comum). Se null (servidor
    // que ja tinha cadastro antes do fluxo), cai pra 0 = mostra tudo.
    const info = readActiveMatricula();
    const iso = info?.contaAtivadaEm;
    if (!iso) return 0;
    const t = Date.parse(iso);
    return Number.isFinite(t) ? t : 0;
  }, []);
  const ofertasFiltradas = useMemo(() => {
    const list = ofertasQ.data?.ofertas ?? [];
    if (contaAtivadaTs === 0) return list;
    return list.filter((o) => {
      const t = Date.parse(o.criadoEm);
      return Number.isFinite(t) && t >= contaAtivadaTs;
    });
  }, [ofertasQ.data, contaAtivadaTs]);
  const notifs = useMemo(
    () => {
      void tickKey; // usado pra re-render periodico dos "ha Xmin" e countdown de promo
      return buildNotificationsFromPropostas(propostas, ofertasFiltradas, portQ.data?.itens ?? []);
    },
    [propostas, ofertasFiltradas, portQ.data, tickKey],
  );

  // Recalcula "ha Xmin/Xh" e countdown de promo relampago. Se ha alguma
  // oferta com expiraEm em <1h, refresh a cada 1s pra countdown suave;
  // 24h -> 15s; caso contrario 30s.
  useEffect(() => {
    const now = Date.now();
    const menorRestante = ofertasFiltradas
      .map((o) => (o.expiraEm ? new Date(o.expiraEm).getTime() - now : Infinity))
      .filter((n) => Number.isFinite(n) && n > 0)
      .reduce((m, n) => Math.min(m, n), Infinity);
    const intervalo = menorRestante < 3600_000 ? 1_000
                    : menorRestante < 24 * 3600_000 ? 15_000
                    : 30_000;
    const i = setInterval(() => setTickKey((k) => k + 1), intervalo);
    return () => clearInterval(i);
  }, [ofertasFiltradas]);

  // Storage: matricula/lidas mudou → forca re-render pra reaplicar filtros.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (
        e.key === STORAGE_KEY_META ||
        e.key === STORAGE_KEY_ID ||
        e.key === "atlas:notifications:read" ||
        e.key === "atlas:notifications:dismissed" ||
        e.key === "atlas:notifications:seen"
      ) {
        setTickKey((k) => k + 1);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Ao trocar de rota, refetch pra pegar mudancas depois de agir (ex.: criou
  // proposta em /termo e voltou).
  useEffect(() => {
    void q.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // UM estado so: nao lida. Governa badge, apito, title da aba E a bolinha
  // dourada do item. Tentar separar "vista" de "lida" (07/08/2026 manha) criou
  // contradicao visivel — badge zerado com 29 bolinhas de nao-lida do lado.
  const naoLidas = notifs.filter((n) => !n.lida).length;

  // FECHAR o dropdown marca como lido tudo que estava listado: o servidor
  // acabou de ver a lista. Marcar ao ABRIR zerava o contador no mesmo instante
  // em que o dropdown aparecia, e o botao "Marcar todas como lidas" nunca
  // chegava a ser clicavel. Assim o sino nao volta a apitar as mesmas
  // notificacoes a cada login, e a acao manual continua disponivel enquanto a
  // lista esta aberta. Cliente 07/08/2026.
  const prevOpenRef = useRef(open);
  useEffect(() => {
    const fechou = prevOpenRef.current && !open;
    prevOpenRef.current = open;
    if (!fechou) return;
    const pendentes = notifs.filter((n) => !n.lida).map((n) => n.id);
    if (pendentes.length === 0) return;
    markAllAsRead(pendentes);
    setTickKey((k) => k + 1);
  }, [open, notifs]);

  // Anima o sino (pulse dourado) toda vez que o contador de nao-vistas SOBE.
  // Alem da animacao, atualiza o title da aba pra "(N) Atlas" quando ha
  // pendencias — assim o usuario ve mesmo com a aba em background.
  const prevUnreadRef = useRef(naoLidas);
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    if (naoLidas > prevUnreadRef.current) {
      setPulse(true);
      const t = setTimeout(() => setPulse(false), 1500);
      // Bipe curto opcional — silencioso se o browser bloquear autoplay.
      try {
        const AudioCtx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (AudioCtx) {
          const ctx = new AudioCtx();
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.frequency.value = 880;
          g.gain.value = 0.05;
          o.connect(g); g.connect(ctx.destination);
          o.start();
          o.stop(ctx.currentTime + 0.12);
          setTimeout(() => ctx.close(), 300);
        }
      } catch { /* autoplay policy */ }
      return () => clearTimeout(t);
    }
    prevUnreadRef.current = naoLidas;
    return;
  }, [naoLidas]);

  useEffect(() => {
    const base = "Atlas";
    document.title = naoLidas > 0 ? `(${naoLidas}) ${base}` : base;
    return () => { document.title = base; };
  }, [naoLidas]);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function abrirNotif(n: Notification) {
    markAsRead(n.id);
    setTickKey((k) => k + 1);
    setOpen(false);
    // Link externo (banco) abre em nova aba; navegacao interna leva pra
    // pagina com hash pra scrollar ate o card especifico.
    if (n.externalLink) {
      window.open(n.externalLink, "_blank", "noopener,noreferrer");
    }
    if (n.href) nav(n.href);
  }

  function marcarTodasLidas() {
    markAllAsRead(notifs.map((n) => n.id));
    setTickKey((k) => k + 1);
  }

  function limparTudo() {
    // Marca todos como dispensados (some do dropdown). Notificacoes futuras
    // (novos estados da proposta) continuam aparecendo — so o snapshot atual
    // e escondido. Mesmo padrao do app.
    dismissAll(notifs.map((n) => n.id));
    setTickKey((k) => k + 1);
  }

  return (
    <div ref={ref} style={{ position: "relative", zIndex: 100 }}>
      <style>{`@keyframes atlas-bell-pulse { 0%{transform:scale(1);box-shadow:0 0 0 0 rgba(212,175,55,.6)} 50%{transform:scale(1.15);box-shadow:0 0 0 8px rgba(212,175,55,0)} 100%{transform:scale(1);box-shadow:0 0 0 0 rgba(212,175,55,0)} }`}</style>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notificações (${naoLidas} não lidas)`}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          position: "relative",
          width: 36,
          height: 36,
          borderRadius: 999,
          background: "transparent",
          border: "1px solid var(--border)",
          color: "var(--text)",
          cursor: "pointer",
          fontSize: 16,
          display: "grid",
          placeItems: "center",
          animation: pulse ? "atlas-bell-pulse 1.4s ease-out" : undefined,
        }}
      >
        🔔
        {naoLidas > 0 ? (
          <span
            style={{
              position: "absolute",
              top: -4,
              right: -4,
              minWidth: 18,
              height: 18,
              padding: "0 5px",
              borderRadius: 999,
              background: "var(--danger-500)",
              color: "white",
              fontSize: 10,
              fontWeight: 700,
              display: "grid",
              placeItems: "center",
              border: "2px solid var(--bg)",
            }}
          >
            {naoLidas}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          role="menu"
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            width: 380,
            maxWidth: "calc(100vw - 32px)",
            background: "var(--surface-solid)",
            border: "1px solid var(--border)",
            borderRadius: 12,
            boxShadow: "0 8px 24px rgba(0,0,0,.25)",
            zIndex: 100,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid var(--border)",
              position: "sticky",
              top: 0,
              background: "var(--surface-solid)",
              zIndex: 1,
            }}
          >
            <span style={{ fontWeight: 700, fontSize: ".95rem" }}>Notificações</span>
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              {naoLidas > 0 ? (
                <button
                  type="button"
                  onClick={marcarTodasLidas}
                  style={{
                    background: "transparent",
                    border: 0,
                    color: "var(--accent)",
                    fontSize: ".82rem",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  Marcar todas como lidas
                </button>
              ) : null}
              {notifs.length > 0 ? (
                <button
                  type="button"
                  onClick={limparTudo}
                  title="Remove todas as notificações atuais do dropdown"
                  style={{
                    background: "transparent",
                    border: 0,
                    color: "var(--text-muted)",
                    fontSize: ".82rem",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  Limpar tudo
                </button>
              ) : null}
            </div>
          </div>

          <div style={{ maxHeight: 460, overflowY: "auto" }}>
            {notifs.length === 0 ? (
              <div style={{ padding: 24, textAlign: "center", color: "var(--text-muted)", fontSize: ".88rem" }}>
                Nenhuma notificação para esta matrícula.
              </div>
            ) : (
              notifs.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => abrirNotif(n)}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "12px 16px",
                    background: n.lida ? "transparent" : "color-mix(in srgb, var(--gold-500) 8%, transparent)",
                    border: 0,
                    borderBottom: "1px solid var(--border)",
                    cursor: "pointer",
                    display: "flex",
                    gap: 12,
                    alignItems: "flex-start",
                    color: "var(--text)",
                  }}
                >
                  <span style={{ fontSize: 20, flexShrink: 0 }}>{ICONS[n.type]}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" }}>
                      <span style={{ fontWeight: n.lida ? 500 : 700, fontSize: ".9rem" }}>
                        {n.titulo}
                        {n.externalLink ? <span style={{ color: "var(--accent)", marginLeft: 4 }}>↗</span> : null}
                      </span>
                      <span style={{ fontSize: ".72rem", color: "var(--text-dim)", flexShrink: 0 }}>{n.quando}</span>
                    </div>
                    <div style={{ fontSize: ".82rem", color: "var(--text-muted)", marginTop: 2, lineHeight: 1.4 }}>
                      {n.mensagem}
                    </div>
                  </div>
                  {!n.lida ? (
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: "var(--gold-500)",
                        marginTop: 6,
                        flexShrink: 0,
                      }}
                    />
                  ) : null}
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
